import { useEffect, useState } from "react";
import {
  useAlertStore,
  useGeneralStore,
  usePopupStore,
  useThemeStore,
  useUserStore,
} from "../../../stores";
import { CashDetailsProps } from "./types";
import { Column, Row, ScrollRow } from "../../containers";
import { PopupBack, PopupClose, QuestionBox } from "../../../popup";
import { Heading } from "../../typography";
import { addComma, cashTypeToString } from "../../../utils";
import { SelectorInput, TextInput } from "../../inputs";
import { Customer, Vec2 } from "../../../types";
import ActivityRenderer from "../../activity";
import { ActivityRendererType } from "../../activity/types";
import { CashApi, HOST_CASH_FILES } from "../../../api";
import OptionList from "../../optionlist";
import { EditIcon, TrashIcon } from "../../../assets/icons";
import CashUpdaterForm from "../cash-updater-form";

const CashDetails = <T extends {}>(props: CashDetailsProps<T>) => {
  const { cash, back, updateCb } = props;

  const theme = useThemeStore((s) => s.theme);
  const customers = useGeneralStore((s) => s.customers);
  const getCustomerById = useGeneralStore((s) => s.getCustomerById);
  const setPopupBody = usePopupStore((s) => s.setPopupBody);
  const setPopupProperties = usePopupStore((s) => s.setPopupProperties);
  const pushAlert = useAlertStore((s) => s.pushAlert);
  const hasUserAccess = useUserStore((s) => s.hasUserAccess);

  const [mousePos, setMousePos] = useState<Vec2>({ x: 0, y: 0 });

  useEffect(() => {
    setPopupProperties({
      style: {
        maxHeight: "80%",
        overflowY: "auto",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const onRightClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ): void => {
    e.preventDefault();
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const onEdit = (): void => {
    if (!hasUserAccess(["EditCash"]))
      return pushAlert("دسترسی غیر مجاز", "error");

    setPopupBody(
      <CashUpdaterForm<CashDetailsProps<T>>
        cash={cash}
        back={{ component: CashDetails, props }}
        updateCb={updateCb}
      />
    );
  };

  const onDelete = (): void => {
    if (!hasUserAccess(["EditCash"]))
      return pushAlert("دسترسی غیر مجاز", "error");

    setPopupBody(
      <QuestionBox<CashDetailsProps<T>>
        question="آیا مطمئن هستید که می خواهید این نقدی را حذف کنید؟"
        close={false}
        back={{ component: CashDetails, props }}
        onNo={() => {
          setPopupBody(<CashDetails {...props} />);
        }}
        onYes={() => {
          CashApi.deleteCash(cash._id.$oid)
            .then(() => {
              setPopupBody(back ? <back.component {...back.props} /> : null);
              if (updateCb) updateCb();
              pushAlert("عملیات موفقیت آمیز");
            })
            .catch((err) => {
              pushAlert(err.msg || "unknown error", "error");
            });
        }}
      />
    );
  };

  return (
    <Column
      style={{ padding: "1rem", position: "relative" }}
      gap={15}
      props={{ onContextMenu: onRightClick }}
    >
      <PopupClose />

      {back ? (
        <PopupBack Component={back.component} props={back.props} />
      ) : (
        <></>
      )}

      <OptionList
        mousePos={mousePos}
        options={[
          {
            title: "ویرایش",
            Icon: EditIcon,
            onClick: onEdit,
          },
          {
            title: "حذف",
            Icon: TrashIcon,
            color: theme.alert,
            iconColor: theme.alert,
            onClick: onDelete,
          },
        ]}
      />

      <Heading color={theme.textOnOverlay} size={18}>
        {`اطلاعات نقدی ${cashTypeToString(cash.type)}`}
      </Heading>

      <Row style={{ width: "100%" }} gap={20} className="factor-activity-row">
        {cash.customer ? (
          <SelectorInput<Customer>
            items={customers}
            uniqueKey={"fullname"}
            selected={getCustomerById(cash.customer.$oid)}
            setSelected={() => {}}
            readOnly
            stringField={"fullname"}
            renderStrings={["fullname", "phone"]}
            compareFn={(c, v) =>
              c.fullname.toLowerCase().includes(v.toLowerCase()) ||
              c.phone.includes(v)
            }
            label="نام مشتری"
          />
        ) : (
          <></>
        )}

        <TextInput
          label="بابت"
          value={cash.description}
          inputProps={{ readOnly: true }}
        />

        <TextInput
          label="مبلغ کل (ریال)"
          value={addComma(cash.total_price)}
          inputProps={{ readOnly: true }}
        />
      </Row>

      <Column style={{ width: "100%" }}>
        <Heading
          size={18}
          textStyles={{ width: "100%", textAlign: "right" }}
          color={theme.textOnOverlay}
        >
          رویداد های این نقدی
        </Heading>
        <ScrollRow gap={20} height={300}>
          {cash.activities.map((activity, idx) => (
            <ActivityRenderer<CashDetailsProps<T>>
              key={idx}
              activity={activity}
              type={ActivityRendererType.Small}
              back={{ component: CashDetails, props }}
            />
          ))}
        </ScrollRow>
      </Column>

      {cash.image ? (
        <Column style={{ width: "100%" }} gap={10}>
          <Heading
            size={18}
            textStyles={{ width: "100%", textAlign: "right" }}
            color={theme.textOnOverlay}
          >
            عکس نقدی
          </Heading>
          <Row
            style={{ width: "100%" }}
            gap={20}
            className="factor-activity-row"
          >
            <img
              alt="cash-img"
              src={`${HOST_CASH_FILES}/${cash.image}`}
              style={{
                width: "100%",
                objectFit: "cover",
              }}
            />
          </Row>
        </Column>
      ) : (
        <></>
      )}
    </Column>
  );
};

export default CashDetails;
